import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { type ListApprovalsResponse, approvalsListApprovals, approvalsListApprovalsQueryKey } from '@column/api';
import { Button, Currencies, Dropdown, Icon, Table, TableColumn, TableVariant, Tooltip } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { Notification } from '~/components';
import { AbsoluteTime } from '~/elements';
import { useReviewAchTransfer, useReviewWireTransfer } from '~/hooks/useTransfers';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { formatTransferType, getTransferIcon, getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { Flex, Inner } from '~/styles';
import { UnifiedTransfer } from '~/typings/API';
import { ReviewTransferRequest } from '~/typings/enum';
import { keysToCamelCase } from '~/util/convertKeys';
import { convertValues } from '~/util/convertValues';

import { TransferReviewModal } from './_components/TransferReviewModal';

const extractTransfers = (page: ListApprovalsResponse | undefined): UnifiedTransfer[] =>
  page?.approvals.map((entry) => convertValues<UnifiedTransfer>(keysToCamelCase(entry.transfer))) ?? [];

interface ApprovalsTableRow {
  id: string;
  amount: number;
  amountDirection: ReturnType<typeof convertTransferAmount>;
  createdAt: Date | string;
  currencyCode?: string;
  description?: string;
  transferType: TransferEntryType;
  viewMoreUrl: string;
}

const ScopeChangeNoticeWrapper = styled.div`
  margin: 0 var(--page-margin) 16px;
`;

const CreatedTimeCell = styled(Table.Cell)`
  position: relative;
  overflow: visible;
`;

const SCOPE_CHANGE_NOTICE_KEY = 'approvals.scopeChangeNoticeDismissed';

const buildRow = ({
  amount,
  createdAt,
  currencyCode,
  description,
  id,
  type: transferType,
}: UnifiedTransfer): ApprovalsTableRow => {
  const isIncoming = false;
  const convertAmount = convertTransferAmount(transferType, isIncoming);

  return {
    id,
    amount: Number(amount ?? 0),
    amountDirection: convertAmount,
    createdAt,
    currencyCode,
    description: description ?? undefined,
    viewMoreUrl: getTransferURL(id, transferType),
    transferType,
  };
};

export const PageTransferApprovalsOverview: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('approvals');
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const [reviewCurrentIndex, setReviewCurrentIndex] = useState(0);
  const [scopeNoticeDismissed, setScopeNoticeDismissed] = useState(
    () => typeof window !== 'undefined' && window.localStorage.getItem(SCOPE_CHANGE_NOTICE_KEY) === 'true'
  );
  const dismissScopeNotice = useCallback(() => {
    setScopeNoticeDismissed(true);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SCOPE_CHANGE_NOTICE_KEY, 'true');
    }
  }, []);

  const { cursor, hasPrevious, onNext, onPrevious, pageIndex, setPageSize } = usePaginationState({
    defaultPageSize: pageSize,
  });

  const queryKey = approvalsListApprovalsQueryKey({
    query: { status: 'pending', starting_after: cursor, limit: pageSize },
  });

  const { data, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data: response } = await approvalsListApprovals({
        query: { status: 'pending', starting_after: cursor, limit: pageSize },
        throwOnError: true,
      });
      return response;
    },
  });

  const transfers = useMemo(() => extractTransfers(data), [data]);
  const hasNext = data?.has_more ?? false;
  const totalResultsCount = data?.total_results_count ?? 0;

  const { createRequest: createAchReviewReq } = useReviewAchTransfer({
    onError: (err) => {
      addDangerNotification({
        content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
      });
    },
  });
  const { createRequest: createWireReviewReq } = useReviewWireTransfer({
    onError: (err) => {
      addDangerNotification({
        content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
      });
    },
  });
  const reviewTransfer = useCallback(
    async (id: string, transferType: TransferEntryType, req: ReviewTransferRequest) => {
      if (transferType === 'ach_credit' || transferType === 'ach_debit') {
        await createAchReviewReq({ id, ...req });
      } else {
        await createWireReviewReq({ id, ...req });
      }
    },
    [createAchReviewReq, createWireReviewReq]
  );

  const handleTimeOptionChange = useCallback(
    (value: TableTimeFormat | undefined) => {
      if (!value) return;
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const columns: TableColumn<ApprovalsTableRow>[] = useMemo(
    () => [
      {
        header: 'Amount',
        accessor: 'amount',
        width: { min: 'min-content', max: 'auto' },
        cell: ({ row, value }) => (
          <Table.AmountCell
            currencyCode={row.currencyCode}
            currencyList={Currencies}
            value={value * row.amountDirection}
            note={row.description}
            isBold
            isColorized
          />
        ),
      },
      {
        header: 'Type',
        accessor: 'transferType',
        width: { min: 'min-content', max: '150px' },
        cell: ({ value }) => (
          <Table.ChipCell value={formatTransferType(value)} variant="subtle" icon={getTransferIcon(value)} />
        ),
      },
      {
        header: (
          <Dropdown
            size="small"
            positionRight
            active={timeFormat}
            options={[
              { label: 'Relative Time', value: TableTimeFormat.Relative },
              { label: 'Date / Time', value: TableTimeFormat.Absolute },
            ]}
            onChange={handleTimeOptionChange}
            customLabel={
              <Flex alignItems="center" gap={4}>
                Created <Icon.ChevronDown />
              </Flex>
            }
          />
        ),
        accessor: 'createdAt',
        width: { min: 'min-content', max: '70px' },
        cell: ({ value }) =>
          timeFormat === TableTimeFormat.Relative ? (
            <Table.TimeCell value={value} />
          ) : (
            <CreatedTimeCell>
              <AbsoluteTime alignment="left" timestamp={value} />
            </CreatedTimeCell>
          ),
      },
    ],
    [handleTimeOptionChange, timeFormat]
  );

  const transferStartIndex = pageIndex * pageSize;

  const openReviewModal = useCallback((startIndex: number) => {
    setReviewCurrentIndex(startIndex);
    setIsApprovalModalOpen(true);
  }, []);

  const tableData = useMemo(() => transfers.map(buildRow), [transfers]);

  const onRowClick = useCallback(
    (row: ApprovalsTableRow) => {
      const idx = transfers.findIndex((transfer) => transfer.id === row.id);
      if (idx < 0) return;
      openReviewModal(transferStartIndex + idx);
    },
    [openReviewModal, transferStartIndex, transfers]
  );

  const handleModalClose = useCallback(() => {
    setIsApprovalModalOpen(false);
    const listQueryId = approvalsListApprovalsQueryKey()[0]._id;
    queryClient.invalidateQueries({
      predicate: ({ queryKey: key }) => (key[0] as { _id?: string } | undefined)?._id === listQueryId,
    });
  }, [queryClient]);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPageSize(value);
      updateTableConfig({ limit: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [setPageSize, updateTableConfig, addDangerNotification]
  );

  return (
    <>
      <TransferReviewModal
        transfers={transfers}
        transferStartIndex={transferStartIndex}
        currentIndex={reviewCurrentIndex}
        totalTransfers={totalResultsCount}
        isOpen={isApprovalModalOpen}
        onClose={handleModalClose}
        onSubmit={async (transferId, transferType, request) => {
          await reviewTransfer(transferId, transferType, request);
          queryClient.setQueryData<ListApprovalsResponse>(queryKey, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              approvals: oldData.approvals.filter((entry) => entry.transfer.id !== transferId),
            };
          });
        }}
      />
      <PageActions>
        <Tooltip
          content="No transfers to review"
          isOpen={transfers.length === 0 ? undefined : false}
          placement="bottom"
        >
          <Button isDisabled={transfers.length === 0} onClick={() => openReviewModal(transferStartIndex)} size="small">
            Review Transfers
          </Button>
        </Tooltip>
      </PageActions>
      {!scopeNoticeDismissed && (
        <ScopeChangeNoticeWrapper>
          <Notification
            color="info"
            variant="light"
            onClose={dismissScopeNotice}
            actionButton={{
              label: 'View',
              onClick: () => navigate(`${ROUTE.TRANSFERS}?status=pre_review`),
            }}
          >
            This page now shows only transfers awaiting your approval. To view all transfers pending review, go to the
            Transfers page.
          </Notification>
        </ScopeChangeNoticeWrapper>
      )}
      <Inner pt={0} px="var(--page-margin)">
        <Table<ApprovalsTableRow>
          columns={columns}
          data={tableData}
          isLoading={isFetching}
          variant={TableVariant.List}
          pagination={{
            hasNext,
            hasPrevious,
            pageIndex,
            pageSize,
            onNext: () => {
              if (transfers.length > 0) onNext({ cursor: transfers[transfers.length - 1].id });
            },
            onPrevious,
            onPageSizeChange: handlePageSizeChange,
          }}
          onRowClick={onRowClick}
          rowAction={(row) => (
            <Button
              icon={<Icon.ChevronRightLarge />}
              iconRight
              onClick={(event: React.SyntheticEvent) => {
                event.stopPropagation();
                navigate(row.viewMoreUrl);
              }}
              size="tiny"
              variant="secondary"
            >
              View Transfer
            </Button>
          )}
        />
      </Inner>
    </>
  );
};
